import React, { useEffect, useState } from "react";
import { InputField } from "../../../../Components/formElements/InputField";
import { Col, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import ItemService from "../../../../services/customer_appCommonService";
import ItemServiceV2 from "../../../../services/customer_v2_appCommonService";
import accountsApi from "../../../../services/accountsCommonService";
import {
  formatDateWithAMPM,
  getTodayDate,
  validateContactNumber,
  validateRequired,
  validPostalCode,
} from "../../../../common/Validations";
import MaterialSelectionModal from "./MaterialSelectionModal";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import TableDropdownMenu from "../../../../Components/TableDropdownMenu/TableDropdownMenu";
import CreditPolicyRequestFormOrderBookingModal from "../CreditPolicy/CreditPolicyRequestFormOrderBookingModal";
import FailureModal from "../../../../Components/Modal/supplier_app/FailureModal";
import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import Layout from "../../../../Layout/Layout";
import { ComboboxField } from "../../../../Components/formElements/ComboboxField";
import CustomerConstants from "../../../../common/CustomerConstants";
import { useNavigate } from "react-router-dom";

const message = {
  VALID_CUSTOMER_NAME: "Special characters or numbers are not allowed.",
  VALID_MAIL_ID: "Please enter a valid email address.",
  VALID_ALTERNATE_MAIL_ID: "Please enter a valid alternate email address.",
  VALID_CONTACT_NUMBER: "Please enter a valid contact number.",
  VALID_ALTERNATE_CONTACT_NUMBER:
    "Please enter a valid alternate contact number.",
  VALID_PURPOSE_JUSTIFICATION:
    "Please ensure the justification is under 500 characters.",
  VALID_POSTAL_CODE: "Enter a 6-character postal code.",
  VALID_CONTACT_FIRST_NAME: "Special characters and numbers are not allowed",
  VALID_CONTACT_LAST_NAME: "Special characters and numbers are not allowed",
  EXIStS_MAIL_ID: "Email already exists",
  EXIStS_CONTACT_NUMBER: "Contact Number already exists",
  EXIStS_ALTERNATE_CONTACT_NUMBER: "Contact Number already exists",
  EXIStS_ALTERNATE_MAIL_ID: "Alternate Contact Number already exists",
  FILL_REQUIRED_FIELD: "Please fill the required field",
  ALTERNATE_MAIL_ID: "Email ID is same",
  ALTERNATE_CONTACT_NUMBER: "Contact number is same",
  GET_MAP_DISTANCE: "Please Click the Get Distance & Time button",
};

const OrderBookingUpdate = React.forwardRef((props, ref) => {
  const navigate = useNavigate();
  let userData = useSelector((state) => state?.user?.data);
  let orderNr = useSelector(
    (state) => state?.customerV1?.order_scheduling.orderSchedulingID,
  );

  const [updateDataMode, setUpdateDataMode] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const getInitialModification = () => ({
    reason: "",
    reasonCode: "",
    reasonDescription: "",
    modifiedDate: getTodayDate(),
    modifiedRemarks: "",
  });

  const [modification, setModification] = useState(getInitialModification());

  const handleEdit = () => {
    setModification(getInitialModification());
    setUpdateDataMode(true);
  };

  const handleReasonChange = (data) => {
    setModification((prev) => ({
      ...prev,
      reason: data?.name || "",
      reasonCode: data?.code || "",
      reasonDescription: data?.description || "",
    }));
  };

  const handleModificationChange = (event) => {
    const { name, value } = event.target;

    setModification((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const exForDropdownData = [
    { id: 1, name: "Ex-Work" },
    { id: 2, name: "FOR" },
  ];
  const priority = [
    { id: 1, code: "LH", name: "Load and Halt" },
    { id: 2, code: "CON", name: "Concrete" },
    { id: 3, code: "CE", name: "Customer Emergency" },
    { id: 4, code: "MED", name: "Medium" },
  ];

  const reasons = [
    {
      id: 1,
      code: "QTY_CHANGE",
      name: "Quantity Change",
    },
    {
      id: 2,
      code: "CUST_CANCEL",
      name: "Customer Cancelled",
    },
    {
      id: 3,
      code: "LOC_CHANGE",
      name: "Location Change",
    },
    {
      id: 4,
      code: "DATE_CHANGE",
      name: "Date Change",
    },
    {
      id: 5,
      code: "DELIVERY_CHANGE",
      name: "Delivery Schedule Change",
    },
    {
      id: 6,
      code: "ITEM_CHANGE",
      name: "Product / Item Change",
    },
    {
      id: 7,
      code: "PRICE_CHANGE",
      name: "Price Change",
    },
    {
      id: 8,
      code: "CUSTOMER_REQUEST",
      name: "Customer Request",
    },
    {
      id: 9,
      code: "DATA_ISSUE",
      name: "Data Entry Issue",
    },
    {
      id: 10,
      code: "TAX_CORRECTION",
      name: "Tax / GST Correction",
    },
    {
      id: 11,
      code: "ADDRESS_CHANGE",
      name: "Billing / Shipping Address Change",
    },
    {
      id: 12,
      code: "CREDIT_LIMIT",
      name: "Credit Limit Issue",
    },
    {
      id: 13,
      code: "STOCK_ISSUE",
      name: "Stock Availability Issue",
    },
    {
      id: 14,
      code: "DUPLICATE_ORDER",
      name: "Duplicate Order",
    },
    {
      id: 15,
      code: "WRONG_CUSTOMER",
      name: "Wrong Customer Selected",
    },
    {
      id: 16,
      code: "OTHER",
      name: "Other",
    },
  ];
  const [customerCredit, setCustomerCredit] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState("");

  const getCustomerCreditByCode = async (customerCode) => {
    if (!customerCode) {
      setCustomerCredit(null);
      setCreditError("");
      return;
    }

    try {
      setCreditLoading(true);
      setCreditError("");

      const result = await accountsApi.customerCreditByCode(
        customerCode.trim(),
      );

      if (result?.status === 200 && result?.data) {
        setCustomerCredit(result.data);
      } else {
        setCustomerCredit(null);
        setCreditError(result?.message || "Customer credit details not found");
      }
    } catch (error) {
      setCustomerCredit(null);
      setCreditError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch customer credit",
      );
    } finally {
      setCreditLoading(false);
    }
  };

  useEffect(() => {
    if (!orderData) return;

    // Other customers do not have a registered customer code
    if (orderData.othersCustomerStatus || !orderData.customerCode) {
      setCustomerCredit(null);
      setCreditError("");
      return;
    }

    // Pass actual customer code, not order number
    getCustomerCreditByCode(orderData.customerCode);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData?.customerCode, orderData?.othersCustomerStatus]);
  const creditLimit = Number(customerCredit?.creditLimit ?? 0);
  const outstanding = Number(customerCredit?.outstanding ?? 0);
  const openOrders = Number(customerCredit?.openOrders ?? 0);
  const productDetailsCol = React.useMemo(() => {
    const ro = !updateDataMode;
    return [
      {
        name: "Sl.No",
        selector: (row) => row.local_id,
        cell: (row, index) => <>{index + 1}.</>,
        width: "80px",
      },
      {
        name: "Material",
        selector: (row) => row.materialLongText,
        sortable: true,
        width: "120px",
        cell: (row) => (
          <div>
            <p className="m-0 p-0 pt-1 fw-bold text-primary">
              {row.materialLongText}
            </p>
            <p className="m-0 p-0">{row.materialCode}</p>
          </div>
        ),
      },
      {
        name: "A/C",
        width: "120px",
        cell: (row) => (
          <div className="form-check form-switch">
            <input
              className="form-check-input custom-switch"
              type="checkbox"
              role="switch"
              id={`switch-${row.local_id}`}
              disabled={ro}
              checked={row.accOnOff === true}
              onChange={(e) => handleCheckboxChange(row.local_id, e)}
            />
          </div>
        ),
      },
      { name: "UOM", selector: (row) => row.uOM || "--", width: "100px" },
      {
        name: "Qty",
        width: "100px",
        cell: (row) => (
          <InputField
            className="inputBox mt-1"
            name="orderQuantityLoad"
            placeholder=""
            disabled={ro}
            value={row.orderQuantityLoad || ""}
            onChange={(e) => handleInputChange(row.local_id, e, "Number")}
          />
        ),
      },
      {
        name: "Unit Price",
        width: "130px",
        cell: (row) => (
          <InputField
            className="inputBox mt-1"
            name="unitPrice"
            placeholder=""
            disabled={ro}
            value={row.unitPrice || ""}
            onChange={(e) => handleInputChange(row.local_id, e, "Number")}
          />
        ),
      },
      {
        name: "EX/FOR",
        width: "200px",
        cell: (row) => (
          <TableDropdownMenu
            index={row?.local_id}
            label={"EX/FOR"}
            data={exForDropdownData}
            id={"exWork"}
            name={"exWork"}
            code={"exWorkCode"}
            description={"exWorkDescription"}
            value={row.exWork}
            disabled={ro}
            onChange={handleDropdownChange}
          />
        ),
      },
      {
        name: "Truck Nr",
        width: "200px",
        cell: (row) =>
          row?.exWork === "Ex-Work" ? (
            <InputField
              className="inputBox"
              name="loadingTruckNo"
              id="loadingTruckNo"
              disabled={ro}
              value={row?.loadingTruckNo || ""}
              onChange={(e) => handleInputChange(row.local_id, e)}
            />
          ) : (
            <>--</>
          ),
      },
      {
        name: "Priority",
        width: "200px",
        cell: (row) => (
          <TableDropdownMenu
            index={row?.local_id}
            label="Priority"
            data={priority}
            id="priority"
            name="priority"
            code="priorityCode"
            description="priorityDescription"
            value={
              row?.priority ??
              row?.priorityCode ??
              row?.priorityDescription ??
              ""
            }
            disabled={ro}
            onChange={handleDropdownChange}
          />
        ),
      },
      {
        name: "Dispatch Date",
        width: "200px",
        cell: (row) => (
          <InputField
            className="inputBox mt-1"
            name="dispatchDate"
            type="date"
            placeholder=""
            disabled={ro}
            value={row.dispatchDate || getTodayDate("")}
            onChange={(e) => handleInputChange(row.local_id, e)}
          />
        ),
      },
      {
        name: "Shipping Address",
        width: "250px",
        cell: (row) => (
          <TextAreaField
            className="inputBox mt-1"
            name="shippingAddress"
            placeholder="Enter Shipping Address"
            disabled={ro}
            value={row.shippingAddress || ""}
            onChange={(e) => handleInputChange(row.local_id, e)}
          />
        ),
      },
      {
        name: "Ship to Party",
        width: "130px",
        cell: (row) => (
          <input
            className="form-check-input"
            type="checkbox"
            disabled={ro}
            checked={Boolean(row?.shipToParty)}
            onChange={(e) => {
              const checked = e.target.checked;

              setProductDetailsData((prev) =>
                prev.map((it) =>
                  it.local_id === row.local_id
                    ? { ...it, shipToParty: checked }
                    : it,
                ),
              );
            }}
          />
        ),
      },
      {
        name: "Pin Code",
        width: "200px",
        cell: (row) => (
          <InputField
            className="inputBox mt-1"
            name="shippingPostalCode"
            placeholder="Enter Pin Code"
            disabled={ro}
            value={row?.shippingPostalCode || ""}
            onChange={(e) => handleInputChange(row.local_id, e, "Number")}
          />
        ),
      },
      {
        name: "Remarks",
        width: "200px",
        cell: (row) => (
          <TextAreaField
            className="inputBox mt-1"
            name="dispatchRemarks"
            placeholder=""
            disabled={ro}
            value={row.dispatchRemarks || ""}
            onChange={(e) => handleInputChange(row.local_id, e, "Text")}
          />
        ),
      },
      {
        name: "Delete",
        width: "120px",
        cell: (row) =>
          row.orderHistory?.length ? (
            <span title="Already loaded, cannot delete">--</span>
          ) : (
            <button
              disabled={ro}
              onClick={() => removeProductDetails(row.local_id)}
            >
              <i className="bi bi-trash text-danger fs-4" />
            </button>
          ),
      },
    ];
    // eslint-disable-next-line
  }, [updateDataMode]);

  const defaultCustomerDetails = {
    customerName: "",
    address: "",
    address2: "",
    address3: "",
    state: "",
    cityPostalCode: "",
    certifications: "",
  };
  const failureModalData = {
    title: "Warning",
    ErrorMsg: "",
  };

  const getInitialFormInputs = () => ({
    orderStatus: "Pending",
    salesOrderStatus: "Created",
    orderPlacedBy: "",
    customerDetails: "",
    emailAddress: "",
    deliveryAddress: "",
    customerDetailsIfOther: "",
    additionalCreditAmt: "",
    createdDate: new Date(),
    createdBy: userData?.userId,
    dispatchDate: getTodayDate(""),
  });

  const [modalShow, setModalShow] = useState(false);
  const [isCardVisible1, setCardVisible1] = useState(true);
  const [isCardVisible2, setCardVisible2] = useState(true);
  const [isCardVisible3, setCardVisible3] = useState(true);
  const [isCardVisible4, setCardVisible4] = useState(true);

  const [getProductDetailsData, setProductDetailsData] = useState([]);

  const [disable, setDisabled] = useState(false);
  const [getCustomerDetails, setCustomerDetails] = useState(null);
  const [getCompanyDetails, setCompanyDetails] = useState(null);
  const [orderTotalValue, setOrderTotalValue] = useState(0);

  const [getShowFailureModal, setShowFailureModal] = useState(false);

  const [getErrorFields, setErrorFields] = useState([]);
  const [getFailureModal] = useState(failureModalData);

  const [getCreditPolicyFormModal, setCreditPolicyFormModal] = useState(false);
  // eslint-disable-next-line
  const [toggleCreditRequest, setToggleCreditRequest] = useState(false);

  const toggleCardVisibility1 = () => {
    setCardVisible1(!isCardVisible1);
  };
  const toggleCardVisibility3 = () => {
    setCardVisible3(!isCardVisible3);
  };
  const toggleCardVisibility4 = () => {
    setCardVisible4(!isCardVisible4);
  };

  const [formInputs, setFormInputs] = useState(getInitialFormInputs());

  const [validation, setValidation] = useState({});

  const toggleCardVisibility2 = () => {
    setCardVisible2(!isCardVisible2);
  };

  const [othersCustomer, setOthersCustomer] = useState(false);
  const onCheckBoxChange = (event) => {
    setOthersCustomer(event.target.checked);
    setCustomerDetails(defaultCustomerDetails);
  };

  const [addOnInv, setAddOnInv] = useState(false);
  const addOnInvoiceChange = (event) => {
    setAddOnInv(event.target.checked);
  };
  const versionHistoryData = React.useMemo(
    () =>
      Object.entries(orderData?.versionHistory || {})
        .map(([version, v]) => ({ ...v, version: v.version || version }))
        .sort(
          (a, b) =>
            Number(String(b.version).replace(/\D/g, "")) -
            Number(String(a.version).replace(/\D/g, "")),
        ),
    [orderData],
  );

  const versionHistoryCol = [
    { name: "Sl.No", selector: (row) => row.slNo, width: "80px" },
    { name: "Version", selector: (row) => row.version, width: "100px" },
    { name: "Reason", selector: (row) => row.reason, width: "220px" },
    {
      name: "Modified Date",
      selector: (row) => row.modifiedDate,
      width: "140px",
    },
    {
      name: "Modified By",
      selector: (row) => row.modifiedByName,
      width: "160px",
    },
    { name: "Remarks", selector: (row) => row.remarks || "--", width: "240px" },
  ];

  const removeProductDetails = (local_id) => {
    setProductDetailsData((prev) =>
      prev.filter((p) => p.local_id !== local_id),
    );
  };

  const handleGetSelectedMaterial = (selectedData) => {
    setProductDetailsData((prevData) => {
      const customerAddress = [
        getCustomerDetails?.address,
        getCustomerDetails?.address2,
        getCustomerDetails?.address3,
        getCustomerDetails?.state,
      ]
        .filter(Boolean)
        .join(" ");
      const postalCode = getCustomerDetails?.cityPostalCode;

      const existingCodes = new Set(prevData.map((i) => i.materialCode));
      let nextId = prevData.reduce(
        (m, i) => Math.max(m, Number(i.local_id) || 0),
        0,
      );

      const newItems = selectedData
        .filter((item) => !existingCodes.has(item.materialCode))
        .map((item) => {
          nextId += 1;
          const { _id, ...rest } = item;
          return {
            ...rest,
            orderPlacedBy: "",
            customerDetails: "",
            emailAddress: "",
            deliveryAddress: "",
            customerDetailsIfOther: "",
            createdDate: new Date(),
            createdBy: userData?.userId,
            customerName: orderData?.customerName,
            customerCode: orderData?.customerCode,
            projectName: orderData?.deliveryName || "",
            companyCode: orderData?.companyCode,
            companyCodeDesc: orderData?.companyCodeDesc,
            local_id: nextId,
            dispatchDate: getTodayDate(""),
            orderStatus: "Pending",
            orderQuantityLoad: "",
            shippingAddress: customerAddress,
            shippingPostalCode: postalCode ? Number(postalCode) : "",
            originalShippingAddress: customerAddress,
            originalShippingPostalCode: postalCode,
            isShippingEdited: false,
            isPostalEdited: false,
            customerAddress,
            customerPostalCode: postalCode,
            companyAddress: orderData?.companyAddress,
            companyPostalCode: orderData?.companyPostalCode,
            companyCertifications: orderData?.companyCertifications,
          };
        });

      return [...prevData, ...newItems];
    });
  };

  const handleInputChange = (local_id, event, type) => {
    const { name, value } = event.target;

    if (type === "Number" && isNaN(value)) {
      toast.error("Please enter a valid number");
      return;
    }

    setProductDetailsData((prev) =>
      prev.map((row) => {
        if (row.local_id !== local_id) return row;

        let updatedRow = { ...row, [name]: value };

        if (name === "shippingAddress") {
          updatedRow.isShippingEdited = true;

          if (value.trim()) {
            updatedRow.shippingPostalCode = "";
            updatedRow.isPostalEdited = true;
          } else {
            updatedRow.shippingAddress = updatedRow.originalShippingAddress;
            updatedRow.shippingPostalCode =
              updatedRow.originalShippingPostalCode || "";
            updatedRow.isPostalEdited = false;
          }
        }

        if (name === "shippingPostalCode") {
          updatedRow.isPostalEdited = true;
          updatedRow.shippingPostalCode = value;
        }

        const qty = parseFloat(updatedRow.orderQuantityLoad) || 0;
        const price = parseFloat(updatedRow.unitPrice) || 0;

        const reversePercentage = parseFloat(updatedRow.reversePercentage) || 1;
        const cgst = parseFloat(updatedRow.cgst) || 0;
        const sgst = parseFloat(updatedRow.sgst) || 0;

        const gst = cgst + sgst;
        updatedRow.gst = gst;

        updatedRow.basicPrice = price / reversePercentage || 0;
        updatedRow.basicAmount = (updatedRow.basicPrice * qty).toFixed(2);

        updatedRow.cgstValue = ((cgst * updatedRow.basicAmount) / 100).toFixed(
          2,
        );
        updatedRow.sgstValue = ((sgst * updatedRow.basicAmount) / 100).toFixed(
          2,
        );
        updatedRow.gstValue = ((gst * updatedRow.basicAmount) / 100).toFixed(2);

        updatedRow.rate = (qty * price).toFixed(2);

        updatedRow.netValue = (
          parseFloat(updatedRow.basicAmount) + parseFloat(updatedRow.gstValue)
        ).toFixed(2);

        return updatedRow;
      }),
    );
  };

  useEffect(() => {
    if (getProductDetailsData.length === 0) {
      setOrderTotalValue("");
      return;
    }
    const total = getProductDetailsData.reduce((sum, row) => {
      const qty = parseFloat(row.orderQuantityLoad) || 0;
      const price = parseFloat(row.unitPrice) || 0;
      return sum + qty * price;
    }, 0);
    setOrderTotalValue(total.toFixed(2));
  }, [getProductDetailsData]);

  const thisOrderAmount = Number(orderTotalValue) || 0;
  const creditAvailable =
    creditLimit - outstanding - openOrders - thisOrderAmount;
  const creditExceeded = creditAvailable < 0;
  const othersOnInputChange = ({ target: { name, value } }) => {
    const errors = {};

    setCustomerDetails((customerDetails) => ({
      ...customerDetails,
      [name]: value,
    }));
    setValidation((prev) => ({
      ...prev,
      [name]: errors[name] || "",
    }));
  };
  // eslint-disable-next-line
  const [currentOutstandingAmt, setCurrentOutstandingAmt] = useState(0);

  useEffect(() => {
    const maxLimit =
      Number(getCustomerDetails?.companyProfile?.creditMaxLimit) || 0;
    const available =
      Number(getCustomerDetails?.companyProfile?.creditAvailableAmt) || 0;

    const outstanding = maxLimit - available;

    setCurrentOutstandingAmt(outstanding.toFixed(2));
  }, [getCustomerDetails]);

  const getCustomerDetail = async (userId) => {
    let obj1 = {
      documentNumber: userId,
    };
    await ItemService.getCustomerDetail(obj1).then((item) => {
      if (item?.status === 200) {
        setCustomerDetails(item?.data[0] || null);
        laodCutomerExtendedPeriod(item?.data[0]?.customerCode || "");
      } else {
        setCustomerDetails([]);
        console.log(">> Error: Loading getCustomerDetail");
      }
    });
  };

  const getCompanyMaster = async (code) => {
    let obj1 = {
      code: code,
    };
    await ItemServiceV2.getCompanyMaster(obj1).then((item) => {
      if (item?.status === 200) {
        setCompanyDetails(item?.data[0] || null);
      } else {
        setCompanyDetails([]);
        console.log(">> Error: Loading getCompanyMaster");
      }
    });
  };

  const handleCheckboxChange = (local_id, event) => {
    const checkedValue = event.target.checked ? true : false;

    setProductDetailsData((prevRows) =>
      prevRows.map((row) =>
        row.local_id === local_id ? { ...row, accOnOff: checkedValue } : row,
      ),
    );
  };

  const handleDropdownChange = (data) => {
    setProductDetailsData((prevRows) =>
      prevRows.map((row) =>
        row.local_id === data.index
          ? {
              ...row,
              [data.name]: data.value,
              [data.code]: data.codeValue,
              [data.desc]: data.descValue,
            }
          : row,
      ),
    );
  };

  const addProductDetails = () => {
    const requiredFields = [];
    const anyFieldNotEmpty = Object.values(validation).some(
      (value) => value !== null && value !== "",
    );
    if (anyFieldNotEmpty) {
      requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
      return;
    }
    if (!validateRequired(formInputs.companyCode)) {
      requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
      setValidation((prev) => ({
        ...prev,
        companyCode: message.FILL_REQUIRED_FIELD,
      }));
      return;
    }
    if (!othersCustomer) {
      if (!validateRequired(formInputs.customerName)) {
        requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
        setValidation((prev) => ({
          ...prev,
          customerName: message.FILL_REQUIRED_FIELD,
        }));
        return;
      }
    } else {
      if (!validateRequired(getCustomerDetails?.customerName)) {
        requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
        setValidation((prev) => ({
          ...prev,
          customerName: message.FILL_REQUIRED_FIELD,
        }));
        return;
      }
      if (!validateRequired(getCustomerDetails?.address)) {
        requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
        setValidation((prev) => ({
          ...prev,
          address: message.FILL_REQUIRED_FIELD,
        }));
        return;
      }
      if (!validateRequired(getCustomerDetails?.cityPostalCode)) {
        requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
        setValidation((prev) => ({
          ...prev,
          cityPostalCode: message.FILL_REQUIRED_FIELD,
        }));
        return;
      }
      if (!validPostalCode(getCustomerDetails?.cityPostalCode)) {
        requiredFields.push(`${message.VALID_POSTAL_CODE}`);
        setValidation((prev) => ({
          ...prev,
          cityPostalCode: message.VALID_POSTAL_CODE,
        }));
        return;
      }
      if (!validateRequired(getCustomerDetails?.otherCustomerContactNumber)) {
        requiredFields.push(`${message.FILL_REQUIRED_FIELD}`);
        setValidation((prev) => ({
          ...prev,
          otherCustomerContactNumber: message.FILL_REQUIRED_FIELD,
        }));
        return;
      }
      if (
        !validateContactNumber(getCustomerDetails?.otherCustomerContactNumber)
      ) {
        requiredFields.push(`${message.VALID_CONTACT_NUMBER}`);
        setValidation((prev) => ({
          ...prev,
          otherCustomerContactNumber: message.VALID_CONTACT_NUMBER,
        }));
        return;
      }
    }
    setModalShow(true);
  };

  const hideModal = () => {
    setModalShow(false);
  };

  const currentAvailableAmt =
    getCustomerDetails?.companyProfile?.creditMaxLimit -
    (getCustomerDetails?.creditOutStandingAmt ?? 0) -
    (getCustomerDetails?.pendingOrderValue ?? 0) +
    Number(getCustomerDetails?.companyProfile?.limitToExtendedAmt ?? 0);
  // eslint-disable-next-line
  const showCreditPolicyRequestForm = () => {
    setCreditPolicyFormModal(true);
  };

  const hideCreditPolicyRequestForm = () => {
    setCreditPolicyFormModal(false);
  };

  const hideFailureModal = () => {
    setShowFailureModal(false);
  };

  useEffect(() => {
    if (!updateDataMode) {
      setErrorFields([]);
      setShowFailureModal(false);
      setToggleCreditRequest(false);
      setDisabled(false);
      return;
    }

    if (
      orderTotalValue === "" ||
      orderTotalValue === null ||
      orderTotalValue === undefined ||
      Number(orderTotalValue) === 0
    ) {
      setErrorFields([]);
      setShowFailureModal(false);
      setDisabled(false);
      setToggleCreditRequest(false);

      setValidation((prev) => {
        const { orderTotalValue, currentAvailableAmt, ...rest } = prev;
        return rest;
      });

      return;
    }

    if (othersCustomer) {
      setErrorFields([]);
      setShowFailureModal(false);
      setDisabled(false);
      setToggleCreditRequest(false);

      setValidation((prev) => {
        const { orderTotalValue, currentAvailableAmt, ...rest } = prev;
        return rest;
      });

      return;
    }
    // eslint-disable-next-line
    const availableAmt = Number(
      getCustomerDetails?.companyProfile?.creditAvailableAmt ??
        getCustomerDetails?.creditAvailableAmt ??
        0,
    );

    // if (availableAmt === 0 || isNaN(availableAmt)) {
    //   setErrorFields([
    //     "No current available amount, Kindly create a credit limit extension request.",
    //   ]);
    //   setShowFailureModal(true);
    //   setDisabled(true);
    //   setToggleCreditRequest(true);

    //   setValidation((prev) => ({
    //     ...prev,
    //     currentAvailableAmt:
    //       "No current available amount. Kindly create a credit limit extension request.",
    //   }));
    //   return;
    // }

    setErrorFields([]);
    setShowFailureModal(false);
    setDisabled(false);
    setToggleCreditRequest(false);

    setValidation((prev) => {
      const { orderTotalValue, currentAvailableAmt, ...rest } = prev;
      return rest;
    });
    // eslint-disable-next-line
  }, [orderTotalValue, othersCustomer, updateDataMode]);

  const [creditExtendedShow, setCreditExtendedShow] = useState(false);
  const [extendPeriodData, setExtendPeriodData] = useState([]);
  const extendPeriodColumn = [
    {
      name: "Sl.no",
      selector: (row, index) => index + 1,
      cell: (row, index) => <>{index + 1}</>,
      sortable: false,
      width: "100px",
    },
    {
      name: "Extended Amt",
      selector: (row) => row?.limitToExtendedAmt || "--",
      cell: (row) => <>{row?.limitToExtendedAmt}</>,
      sortable: true,
      width: "140px",
    },
    {
      name: "Extended Days",
      selector: (row) => row?.periodToExtendedDays || "--",
      cell: (row) => <>{row?.periodToExtendedDays}</>,
      sortable: true,
      width: "140px",
    },
    {
      name: "Status",
      selector: (row) => row?.cpHeaderStatus || "--",
      cell: (row) => (
        <>
          <TableStatusField status={row?.cpHeaderStatus} />
        </>
      ),
      sortable: true,
      width: "160px",
    },
    {
      name: "Credit Limit",
      selector: (row) => row?.companyProfile?.creditMaxLimit || "--",
      cell: (row) => (
        <div>
          <span>{row?.companyProfile?.creditMaxLimit}</span>
        </div>
      ),
      sortable: true,
      width: "130px",
    },
    {
      name: "Update At",
      selector: (row) => row?.updatedAt || "--",
      cell: (row) => (
        <div>
          <span>{formatDateWithAMPM(row?.updatedAt)}</span>
        </div>
      ),
      sortable: true,
      width: "140px",
    },
    {
      name: "Remark",
      selector: (row) => row?.remark || "--",
      cell: (row) => (
        <div>
          <span>{row?.remark}</span>
        </div>
      ),
      sortable: true,
      width: "130px",
    },
  ];
  // eslint-disable-next-line
  const toggleCreditExtended = () => {
    setCreditExtendedShow(!creditExtendedShow);
  };

  const laodCutomerExtendedPeriod = async (customerId) => {
    const obj = {
      queryName: "getCreditPolicyListbyCustomer",
      rowFilter: {
        $and: [
          {
            customerId: customerId,
          },
        ],
      },
      page: 1,
      per_page: 99999,
    };
    await ItemServiceV2.generateCustomerQuery(obj).then((item) => {
      if (item?.status === 200) {
        toast.success(item?.message || "Customer Extend Period Load ", {
          duration: 2000,
          position: "top-right",
        });
        setExtendPeriodData(item?.data);
      } else {
        toast.error(item?.message || "Please try again !!", {
          duration: 4000,
          position: "top-right",
        });
      }
    });
  };

  useEffect(() => {
    orderDetails();
    // eslint-disable-next-line
  }, []);

  const orderDetails = async () => {
    const obj = {
      queryName: "Get_OrderScheduleBooking_Details",
      documentNumber: orderNr,
    };
    await ItemServiceV2.generateCustomerQuery(obj).then((item) => {
      const order = item?.data?.[0];
      if (item?.status === 200 && order) {
        setOrderData(order);
        setOthersCustomer(!!order.othersCustomerStatus);
        setAddOnInv(!!order.addOnInvoiceStatus);

        const items = Array.isArray(order.items) ? order.items : [order.items];
        const loadedItems = items.filter(Boolean);
        setProductDetailsData(
          loadedItems.map((it, i) => ({ ...it, local_id: it.local_id ?? i + 1 })),
        );

        setFormInputs((prev) => ({
          ...prev,
          companyCode: order.companyCode,
          companyCodeDesc: order.companyCodeDesc,
          customerCode: order.customerCode,
          customerName: order.customerName,
          additionalCreditAmt: order.additionalCreditAmt || "",
        }));

        getCompanyMaster(order.companyCode);
        if (order.othersCustomerStatus) {
          setCustomerDetails({
            customerName: order.customerName,
            address: order.address,
            cityPostalCode: order.cityPostalCode,
            otherCustomerContactName: order.otherCustomerContactName,
            otherCustomerContactNumber: order.otherCustomerContactNumber,
          });
        } else {
          getCustomerDetail(order.customerCode);
        }
      } else {
        toast.error(item?.message || "Unable to load order", {
          duration: 4000,
          position: "top-right",
        });
      }
    });
  };

  const handleCancel = () => {
    setModification(getInitialModification());
    setUpdateDataMode(false);
    orderDetails();
  };

  const handleUpdate = async () => {
    if (getProductDetailsData.length === 0) {
      toast.error("Please add atleast one record!", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }
    const items = getProductDetailsData.map(({ _id, ...rest }) => rest);

    if (
      items.some(
        (i) =>
          !i.orderQuantityLoad || i.orderQuantityLoad.toString().trim() === "",
      )
    ) {
      toast.error("Bag value should not be empty !!", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }
    if (items.some((i) => !i.unitPrice || Number(i.unitPrice) === 0)) {
      toast.error("Unit price should not be empty or zero !!", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }
    if (
      items.some(
        (i) => i.exWork === "Ex-Work" && !i.loadingTruckNo?.toString().trim(),
      )
    ) {
      toast.error("Truck No is required for Ex-Work orders!", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }
    if (
      items.some(
        (i) =>
          i.shippingAddress?.trim() && !i.shippingPostalCode?.toString().trim(),
      )
    ) {
      toast.error("Please fill the Shipping Postal Code", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }

    const { _id, items: _oldItems, version, ...header } = orderData;
    const obj = {
      data: {
        ...header,
        orderTotalValue: parseFloat(orderTotalValue) || "",
        additionalCreditAmt: formInputs?.additionalCreditAmt || "",
        items,
        modification,
      },
    };

    setDisabled(true);
    try {
      const res = await ItemServiceV2.updateOrderScheduleBooking(obj);
      if (res?.status === 200) {
        toast.success(res?.message || "updated successfully", {
          duration: 2000,
          position: "top-right",
        });
        setUpdateDataMode(false);
        orderDetails();
        navigate(CustomerConstants.CUSTOMER_V2_ORDER_BOOKING_LIST_LINE_ITEM);
      } else {
        toast.error(res?.message || "Please try again !!", {
          duration: 4000,
          position: "top-right",
        });
      }
    } catch (e) {
      toast.error("Something went wrong. Please try again !!", {
        duration: 4000,
        position: "top-right",
      });
    } finally {
      setDisabled(false);
    }
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide quoation">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">Order Booking Details</h2>
            <p className="subText"> Order Booking Details</p>
            <hr />
            <Row className="update-action-row">
              <Col
                xs={12}
                className="d-flex justify-content-end align-items-center gap-3"
              >
                {!updateDataMode ? (
                  <button
                    type="button"
                    className="btn btnInfo edit-action-btn"
                    onClick={handleEdit}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btnInfo update-header-btn"
                      onClick={handleUpdate}
                      disabled={disable}
                    >
                      Update
                    </button>

                    <button
                      type="button"
                      className="btn update-cancel-btn"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </Col>
            </Row>
            <div className="accordion mb-4" id="accordionCustomerDetails">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible1
                            ? "bi-chevron-right"
                            : "bi-chevron-down"
                        }`}
                        onClick={toggleCardVisibility1}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">CD</span>
                    </Col>
                    <Col xs={12} md={9} lg={9}>
                      <span className="headerTitle">Customer Details</span>
                      <span className="subHeaderTitle">Customer Details</span>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible1 ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2 pb-4">
                      <Col
                        xs={12}
                        md={6}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <InputField
                          className="inputBox"
                          label="Company Name"
                          id="companyName"
                          name="companyName"
                          value={orderData?.companyCodeDesc || ""}
                          disabled={true}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={6}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <TextAreaField
                          className="inputBox"
                          label="Company Address"
                          name="companyAddress"
                          value={[
                            getCompanyDetails?.address,
                            getCompanyDetails?.address2,
                            getCompanyDetails?.address3,
                            getCompanyDetails?.city,
                            getCompanyDetails?.state,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          disabled={true}
                        />
                      </Col>

                      {!othersCustomer && (
                        <>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Customer Name"
                              id="customerName"
                              name="customerName"
                              value={orderData?.customerName || ""}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <TextAreaField
                              className="inputBox"
                              label="Customer Address"
                              name="projectAddress"
                              value={[
                                getCustomerDetails?.address,
                                getCustomerDetails?.address2,
                                getCustomerDetails?.address3,
                                getCustomerDetails?.state,
                                getCustomerDetails?.cityPostalCode,
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Customer Code"
                              id="customerCode"
                              name="customerCode"
                              value={getCustomerDetails?.customerCode}
                              onChange={othersOnInputChange}
                              disabled={true}
                            />
                          </Col>
                        </>
                      )}

                      <Col
                        xs={12}
                        md={12}
                        lg={12}
                        className="commLeftRightPadding d-flex align-items-center"
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="othersCustomer"
                          checked={othersCustomer}
                          onChange={onCheckBoxChange}
                          disabled={true}
                        />
                        <p className="m-0 ps-1 fw-medium text-dark ">Others </p>
                      </Col>

                      {othersCustomer && (
                        <>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Customer Name"
                              id="customerName"
                              name="customerName"
                              value={getCustomerDetails?.customerName}
                              onChange={othersOnInputChange}
                              required={true}
                              error={validation.customerName}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <TextAreaField
                              className="inputBox"
                              label="Address"
                              id="address"
                              name="address"
                              value={getCustomerDetails?.address}
                              onChange={othersOnInputChange}
                              error={validation.address}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Pin Code"
                              id="cityPostalCode"
                              name="cityPostalCode"
                              value={getCustomerDetails?.cityPostalCode}
                              onChange={othersOnInputChange}
                              error={validation.cityPostalCode}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Contact Name"
                              id="otherCustomerContactName"
                              name="otherCustomerContactName"
                              value={
                                getCustomerDetails?.otherCustomerContactName
                              }
                              onChange={othersOnInputChange}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Contact Number"
                              id="otherCustomerContactNumber"
                              name="otherCustomerContactNumber"
                              value={
                                getCustomerDetails?.otherCustomerContactNumber
                              }
                              onChange={othersOnInputChange}
                              error={validation.otherCustomerContactNumber}
                              disabled={true}
                            />
                          </Col>
                        </>
                      )}

                      <Col
                        xs={12}
                        md={12}
                        lg={12}
                        className="commLeftRightPadding d-flex align-items-center"
                      >
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="addOnInv"
                          checked={addOnInv}
                          onChange={addOnInvoiceChange}
                          disabled={true}
                        />
                        <p className="m-0 ps-1 fw-medium text-dark ">
                          Add-on Invoice{" "}
                        </p>
                      </Col>
                      {addOnInv && (
                        <>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Inv Customer Name"
                              id="invcustomerName"
                              name="invcustomerName"
                              value={getCustomerDetails?.invcustomerName}
                              onChange={othersOnInputChange}
                              required={true}
                              error={validation.invcustomerName}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <TextAreaField
                              className="inputBox"
                              label="Inv Address"
                              id="invAddress"
                              name="invAddress"
                              value={getCustomerDetails?.invAddress}
                              onChange={othersOnInputChange}
                              error={validation.invAddress}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Inv Pin Code"
                              id="invCityPostalCode"
                              name="invCityPostalCode"
                              value={getCustomerDetails?.invCityPostalCode}
                              onChange={othersOnInputChange}
                              error={validation.invCityPostalCode}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Contact Name"
                              id="otherCustomerContactName"
                              name="otherCustomerContactName"
                              value={
                                getCustomerDetails?.otherCustomerContactName
                              }
                              onChange={othersOnInputChange}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <InputField
                              className="inputBox"
                              label="Contact Number"
                              id="otherCustomerContactNumber"
                              name="otherCustomerContactNumber"
                              value={
                                getCustomerDetails?.otherCustomerContactNumber
                              }
                              onChange={othersOnInputChange}
                              error={validation.otherCustomerContactNumber}
                              disabled={true}
                            />
                          </Col>
                        </>
                      )}
                    </Row>
                  </div>
                </div>
              </div>
            </div>
            {/* 
            {!othersCustomer && getCustomerDetails && (
              <div className="accordion mb-4" id="accordionCreditLimit">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${
                            isCardVisible3
                              ? "bi-chevron-right"
                              : "bi-chevron-down"
                          }`}
                          onClick={toggleCardVisibility3}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">CL</span>
                      </Col>
                      <Col xs={12} md={9} lg={9}>
                        <span className="headerTitle">
                          Customer Credit Limit
                        </span>
                        <span className="subHeaderTitle">
                          Credit Limit Details
                        </span>
                      </Col>
                    </Row>
                  </h2>

                  <div
                    className={`accordion-collapse collapse show ${
                      isCardVisible3 ? "visible" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <Row className="pt-2 pb-4">
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Credit Limit Amt"
                            id="creditLimitAmt"
                            name="creditLimitAmt"
                            value={
                              getCustomerDetails?.companyProfile?.creditMaxLimit
                            }
                            disabled={true}
                            error={validation.creditLimitAmt}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Current Outstanding Amt (Invoice)"
                            id="creditOutStandingAmt"
                            name="creditOutStandingAmt"
                            value={
                              getCustomerDetails?.creditOutStandingAmt ?? 0
                            }
                            disabled={true}
                            error={validation.creditOutStandingAmt}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Credit Period (Days)"
                            id="creditPeriod"
                            name="creditPeriod"
                            value={
                              getCustomerDetails?.companyProfile?.creditPeriod
                            }
                            disabled={true}
                            error={validation.creditPeriod}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Order Value"
                            id="orderTotalValue"
                            name="orderTotalValue"
                            value={orderTotalValue}
                            disabled={true}
                            error={validation.orderTotalValue}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Current Order Amt (No Invoice)"
                            id="pendingOrderValue"
                            name="pendingOrderValue"
                            value={getCustomerDetails?.pendingOrderValue ?? 0}
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Credit Available Amt + Extended Amt"
                            id="currentAvailableAmt"
                            name="currentAvailableAmt"
                            value={currentAvailableAmt}
                            disabled={true}
                            error={validation.currentAvailableAmt}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox text-danger"
                            label="Limit to be Extended (Amount)"
                            name="limitToExtendedAmt"
                            value={
                              getCustomerDetails?.companyProfile
                                ?.limitToExtendedAmt ?? ""
                            }
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox text-danger"
                            label="Period to be Extended (Days)"
                            name="periodToExtendedDays"
                            value={
                              getCustomerDetails?.companyProfile
                                ?.periodToExtendedDays ?? ""
                            }
                            disabled={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <button
                            className="btn btn-table"
                            onClick={toggleCreditExtended}
                          >
                            Credit Extended
                          </button>
                        </Col>
                        {toggleCreditRequest && (
                          <Col
                            xs={12}
                            md={6}
                            lg={4}
                            className="commTopButtonRightLeftPadding"
                          >
                            <button
                              className="btn btnInfo mr-4"
                              onClick={() => showCreditPolicyRequestForm()}
                            >
                              Credit Request
                            </button>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
            )} */}

            {/* Company Profile */}
            <div className="accordion mb-4" id="accordionCreditLimit">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible3
                            ? "bi-chevron-right"
                            : "bi-chevron-down"
                        }`}
                        onClick={toggleCardVisibility3}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">CL</span>
                    </Col>
                    <Col xs={12} md={9} lg={9}>
                      <span className="headerTitle">Customer Credit Limit</span>
                      <span className="subHeaderTitle">
                        Credit Limit Details
                      </span>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible3 ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <Row className="pt-2 pb-4">
                      <div className="mt-3 mb-4">
                        <div
                          className="card border-0 shadow-sm"
                          style={{
                            borderRadius: "12px",
                            overflow: "hidden",
                          }}
                        >
                          {/* Header */}
                          <div
                            className="px-3 py-2 d-flex justify-content-between align-items-center"
                            style={{
                              background: creditExceeded
                                ? "#fff1f2"
                                : "#f8fafc",
                              borderBottom: "1px solid #e5e7eb",
                            }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <div
                                style={{
                                  width: "34px",
                                  height: "34px",
                                  borderRadius: "8px",
                                  background: creditExceeded
                                    ? "#fee2e2"
                                    : "#e0f2fe",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className={`bi ${
                                    creditExceeded
                                      ? "bi-exclamation-triangle"
                                      : "bi-credit-card"
                                  }`}
                                  style={{
                                    fontSize: "17px",
                                    color: creditExceeded
                                      ? "#dc2626"
                                      : "#0369a1",
                                  }}
                                />
                              </div>

                              <div>
                                <div
                                  style={{
                                    fontSize: "16px",
                                    color: "#6b7280",
                                  }}
                                >
                                  Credit availability
                                </div>
                              </div>
                            </div>

                            {creditLoading && (
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                />
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                  }}
                                >
                                  Loading...
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Body */}
                          <div style={{ padding: "20px 16px 16px" }}>
                            {creditError ? (
                              <div className="alert alert-danger mb-0 py-2">
                                <i className="bi bi-exclamation-circle me-2" />
                                {creditError}
                              </div>
                            ) : customerCredit ? (
                              <>
                                <div className="row g-3" style={{ marginTop: "12px" }}>
                                  {/* Credit Limit */}
                                  <div className="col-md-3">
                                    <div
                                      className="h-100"
                                      style={{
                                        background: "#f8fafc",
                                        borderRadius: "10px",
                                        border: "1px solid #e5e7eb",
                                        padding: "28px 18px 22px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          marginBottom: "14px",
                                        }}
                                      >
                                        Credit Limit
                                      </div>

                                      <div
                                        style={{
                                          fontSize: "18px",
                                          fontWeight: 700,
                                          color: "#111827",
                                        }}
                                      >
                                        ₹{creditLimit.toFixed(2)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Outstanding */}
                                  <div className="col-md-3">
                                    <div
                                      className="h-100"
                                      style={{
                                        background: "#fff7ed",
                                        borderRadius: "10px",
                                        border: "1px solid #fed7aa",
                                        padding: "28px 18px 22px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#9a3412",
                                          marginBottom: "14px",
                                        }}
                                      >
                                        Outstanding
                                      </div>

                                      <div
                                        style={{
                                          fontSize: "18px",
                                          fontWeight: 700,
                                          color: "#c2410c",
                                        }}
                                      >
                                        ₹{outstanding.toFixed(2)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Open Orders */}
                                  <div className="col-md-3">
                                    <div
                                      className="h-100"
                                      style={{
                                        background: "#eff6ff",
                                        borderRadius: "10px",
                                        border: "1px solid #bfdbfe",
                                        padding: "28px 18px 22px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#1d4ed8",
                                          marginBottom: "14px",
                                        }}
                                      >
                                        Open Orders
                                      </div>

                                      <div
                                        style={{
                                          fontSize: "18px",
                                          fontWeight: 700,
                                          color: "#1d4ed8",
                                        }}
                                      >
                                        ₹{openOrders.toFixed(2)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* This order */}
                                  <div className="col-md-3">
                                    <div
                                      className="h-100"
                                      style={{
                                        background: "#f5f3ff",
                                        borderRadius: "10px",
                                        border: "1px solid #ddd6fe",
                                        padding: "28px 18px 22px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6d28d9",
                                          marginBottom: "14px",
                                        }}
                                      >
                                        This Order
                                      </div>

                                      <div
                                        style={{
                                          fontSize: "18px",
                                          fontWeight: 700,
                                          color: "#6d28d9",
                                        }}
                                      >
                                        ₹{thisOrderAmount.toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Bottom Credit Summary */}
                                <div
                                  className="d-flex justify-content-between align-items-center"
                                  style={{
                                    borderRadius: "10px",
                                    background: creditExceeded
                                      ? "#fef2f2"
                                      : "#f0fdf4",
                                    border: `1px solid ${
                                      creditExceeded ? "#fecaca" : "#bbf7d0"
                                    }`,
                                    marginTop: "18px",
                                    padding: "22px 16px 18px",
                                  }}
                                >
                                  <div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#6b7280",
                                        marginBottom: "14px",
                                      }}
                                    >
                                      Credit available after this order
                                    </div>

                                    <div
                                      style={{
                                        fontSize: "22px",
                                        fontWeight: 700,
                                        color: creditExceeded
                                          ? "#dc2626"
                                          : "#15803d",
                                      }}
                                    >
                                      ₹{creditAvailable.toFixed(2)}
                                    </div>
                                  </div>

                                  <div className="text-end">
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#6b7280",
                                        marginBottom: "14px",
                                      }}
                                    >
                                      Credit Days
                                    </div>

                                    <div
                                      style={{
                                        fontSize: "16px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {customerCredit?.creditDays ?? 0} Days
                                    </div>
                                  </div>

                                  <div>
                                    {creditExceeded ? (
                                      <span className="badge bg-danger px-3 py-2">
                                        <i className="bi bi-x-circle me-1" />
                                        Credit Exceeded
                                      </span>
                                    ) : (
                                      <span className="badge bg-success px-3 py-2">
                                        <i className="bi bi-check-circle me-1" />
                                        Credit Available
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div
                                className="text-center py-3"
                                style={{
                                  color: "#6b7280",
                                  fontSize: "13px",
                                }}
                              >
                                Credit details not available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Row>
                  </div>
                </div>
              </div>
            </div>

            {creditExtendedShow && (
              <div className="accordion mb-4" id="accordionCreditExtended">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${
                            isCardVisible2
                              ? "bi-chevron-right"
                              : "bi-chevron-down"
                          }`}
                          onClick={toggleCardVisibility2}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">CE</span>
                      </Col>
                      <Col xs={12} md={9} lg={9}>
                        <span className="headerTitle">
                          Credit Extended List
                        </span>
                        <span className="subHeaderTitle">
                          Credit Extended List Details
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className={`accordion-collapse collapse show ${
                      isCardVisible2 ? "visible" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <Row>
                        <Col xs={12} md={12} lg={12}>
                          <DataTable
                            title=""
                            columns={extendPeriodColumn}
                            data={extendPeriodData}
                            pagination
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              className="accordion sri-venkateshwara mb-4"
              id="accordionProducts"
            >
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible2
                            ? "bi-chevron-right"
                            : "bi-chevron-down"
                        }`}
                        onClick={toggleCardVisibility2}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">PD</span>
                    </Col>
                    <Col xs={12} md={3} lg={3}>
                      <span className="headerTitle">Product Details</span>
                      <span className="subHeaderTitle">Product Details</span>
                    </Col>
                    <Col xs={12} md={8} lg={8} className="headerButtonAlign">
                      <Col xs={4} md={2} lg={2}>
                        <button
                          className="btnTable"
                          onClick={addProductDetails}
                          disabled={!updateDataMode}
                        >
                          <i className="bi bi-plus-lg"></i> Add
                        </button>
                      </Col>
                    </Col>
                  </Row>
                </h2>
                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible2 ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <Row>
                      <Col xs={12} md={12} lg={12}>
                        <DataTable
                          title=""
                          columns={productDetailsCol}
                          data={getProductDetailsData}
                          pagination
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>
            <div className="accordion mb-4" id="accordionCreditLimit">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible4
                            ? "bi-chevron-right"
                            : "bi-chevron-down"
                        }`}
                        onClick={toggleCardVisibility4}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">MC</span>
                    </Col>
                    <Col xs={12} md={9} lg={9}>
                      <span className="headerTitle">
                        Modification and Collaboration
                      </span>
                      <span className="subHeaderTitle">
                        Modification and Collaboration
                      </span>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible4 ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    {updateDataMode && (
                      <Row className="pt-2 pb-4">
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <ComboboxField
                            label="Reason"
                            placeholder=""
                            data={reasons}
                            id="reason"
                            iconClassName="dropdownIcon"
                            name="reason"
                            code="reasonCode"
                            description="reasonDescription"
                            setValue={modification.reason || ""}
                            getvalue={handleReasonChange}
                            required={true}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Modified Date"
                            type="date"
                            id="modifiedDate"
                            name="modifiedDate"
                            value={modification.modifiedDate}
                            onChange={handleModificationChange}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <TextAreaField
                            className="inputBox"
                            label="Remarks"
                            id="modifiedRemarks"
                            name="modifiedRemarks"
                            value={modification.modifiedRemarks}
                            onChange={handleModificationChange}
                          />
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col xs={12} md={12} lg={12}>
                        <DataTable
                          title=""
                          columns={versionHistoryCol}
                          data={versionHistoryData}
                          pagination
                          noDataComponent="No modifications yet"
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <MaterialSelectionModal
        show={modalShow}
        onHide={() => hideModal()}
        onselectedcatdata={handleGetSelectedMaterial}
      />
      <CreditPolicyRequestFormOrderBookingModal
        show={getCreditPolicyFormModal}
        onHide={() => hideCreditPolicyRequestForm()}
        getCustomerDetails={getCustomerDetails}
        getCompanyDetails={getCompanyDetails}
        currentAvailableAmt={currentAvailableAmt}
      />
      <FailureModal
        show={getShowFailureModal}
        onHide={() => hideFailureModal()}
        failuremodaldata={getFailureModal}
        requiredfields={getErrorFields}
      />
    </main>
  );
});

export default OrderBookingUpdate;
